import React, { useState, useEffect, useRef, use } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { PopupModal } from "../PopupModal/PopupModal";
import { useDispatch } from "react-redux";
import axiosConfig from "../../Service/AxiosConfig";
import { updateTempForm } from "../../redux/BookingFormSlice";
import { FaChevronDown } from "react-icons/fa";
import Select from "react-select";
const PatientDetails = ({ form, setForm, isLocked }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [editModePatient, setEditModePatient] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [popupType, setPopupType] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const firstFieldRef = useRef(null);
  const hasFocusedOnce = useRef(false);
  const ageOptions = Array.from({ length: 120 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  const heightOptions = [];
  for (let feet = 1; feet <= 9; feet++) {
    const maxInch = feet === 9 ? 0 : 11;
    for (let inch = 0; inch <= maxInch; inch++) {
      const value = `${feet}'${inch}`;
      heightOptions.push({
        value,
        label: value,
      });
    }
  }
  const weightOptions = Array.from({ length: 200 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} kg`,
  }));

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const fieldsToCheck = [
    "full_name",
    "patient_age",
    "patient_gender",
    "weight",
    "height",
  ];
  const hasAnyData = fieldsToCheck.some(
    (key) => form?.[key] && String(form[key]).trim() !== "",
  );
  useEffect(() => {
    if (!form?.id) return;
    if (!isDirty) {
      setEditModePatient(!hasAnyData);
    }
  }, [form?.id]);

  useEffect(() => {
    if (!expanded) return;
    const isApiDataEmpty = fieldsToCheck.every(
      (key) => !form?.[key] || String(form[key]).trim() === "",
    );
    if ((isApiDataEmpty || !hasFocusedOnce.current) && firstFieldRef.current) {
      setTimeout(() => {
        firstFieldRef.current.focus();
        hasFocusedOnce.current = true;
      }, 0);
    }
  }, [expanded, form]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!editModePatient) {
      setEditModePatient(true);
    }
    setIsDirty(true);
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    dispatch(
      updateTempForm({
        booking_id: form.booking_id,
        data: { [name]: value },
      }),
    );
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = new FormData();
      payload.append("full_name", form.full_name || "");
      payload.append("patient_age", form.patient_age || "");
      payload.append("patient_gender", form.patient_gender || "");
      payload.append("weight", form.weight || "");
      payload.append("height", form.height || "");
      await axiosConfig.patch(`/accounts/patients/${form?.id}/`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const refreshed = await axiosConfig.get(
        `/accounts/patients/${form?.id}/`,
      );
      setForm((prev) => ({
        ...prev,
        ...refreshed.data,
      }));
      setEditModePatient(false);
      setIsDirty(false);
      setExpanded(false);
      setPopupType("success");
      setMessage("Patient Details Saved Successfully");
    } catch (error) {
      setPopupType("error");
      setError(error?.response?.data?.error || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Accordion
        key={expanded ? "open" : "closed"}
        allowZeroExpanded
        preExpanded={expanded ? ["patient"] : []}
        onChange={(uuids) => {
          setExpanded(uuids.includes("patient"));
        }}
      >
        <AccordionItem uuid="patient">
          <AccordionItemHeading>
            <AccordionItemButton className="bd-header">
              <h4 className="bd-title">Patient Details</h4>
              <span className="bd-add-btn">
                Add Details
                <FaChevronDown
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                    marginLeft: "5px",
                  }}
                />
              </span>
            </AccordionItemButton>
          </AccordionItemHeading>

          <AccordionItemPanel className="bd-panel">
            <div
              className={`bd-section-grid bd-grid-3 service-cards ${
                isLocked ? "locked-section" : ""
              }`}
            >
              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Patient Name
                </label>
                <input
                  ref={firstFieldRef}
                  name="full_name"
                  value={form?.full_name || ""}
                  placeholder="Patient Name"
                  onChange={handleChange}
                  disabled={!editModePatient || isLocked}
                  className={`form-control ${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Patient Age
                </label>

                <Select
                  options={ageOptions}
                  value={ageOptions.find(
                    (opt) => opt.value === form?.patient_age,
                  )}
                  onChange={(selected) => {
                    if (!selected) return;

                    handleChange({
                      target: {
                        name: "patient_age",
                        value: selected.value,
                      },
                    });
                  }}
                  isDisabled={!editModePatient || isLocked}
                  placeholder="Select age"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={`${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Gender
                </label>

                <Select
                  options={genderOptions}
                  value={genderOptions.find(
                    (opt) => opt.value === form?.patient_gender,
                  )}
                  onChange={(selected) => {
                    if (!selected) return;

                    handleChange({
                      target: {
                        name: "patient_gender",
                        value: selected.value,
                      },
                    });
                  }}
                  isDisabled={!editModePatient || isLocked}
                  placeholder="Select gender"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={`${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Weight (Kg)
                </label>
                <Select
                  options={weightOptions}
                  value={weightOptions.find(
                    (opt) => opt.value === Number(form?.weight),
                  )}
                  onChange={(selected) =>
                    handleChange({
                      target: {
                        name: "weight",
                        value: selected.value,
                      },
                    })
                  }
                  isDisabled={!editModePatient || isLocked}
                  placeholder="Patient weight"
                  maxMenuHeight={180}
                  menuPlacement="auto"
                  classNamePrefix="height-select"
                  className={`${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>
              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Height
                </label>
                <Select
                  options={heightOptions}
                  value={heightOptions.find(
                    (opt) => opt.value === form?.height,
                  )}
                  onChange={(selected) =>
                    handleChange({
                      target: {
                        name: "height",
                        value: selected.value,
                      },
                    })
                  }
                  isDisabled={!editModePatient || isLocked}
                  placeholder="Patient height"
                  maxMenuHeight={180}
                  menuPlacement="auto"
                  classNamePrefix="height-select"
                  className={`${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="bd-actions">
                {!isLocked && !editModePatient ? (
                  <button
                    className="bd-edit-btn"
                    onClick={() => {
                      setEditModePatient(true);
                      setIsDirty(true);
                      setExpanded(true);
                    }}
                  >
                    Edit
                  </button>
                ) : (
                  !isLocked &&
                  hasAnyData &&
                  editModePatient && (
                    <button
                      className="bd-save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      Save
                    </button>
                  )
                )}
              </div>
            </div>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
      {popupType && (
        <PopupModal
          setpopupType={setPopupType}
          popupType={popupType}
          error={error}
          Message={message}
          setMessage={setMessage}
        />
      )}
    </>
  );
};

export default PatientDetails;
